import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, Pause, Play, RotateCw, ExternalLink, PlugZap, Trash2, FileText, X, Pause as PauseIcon, LogIn } from 'lucide-react';
import api from '../../services/api';
import StatusPill from '../../components/StatusPill';
import CopyChip from '../../components/CopyChip';

export default function AdminUsers() {
  const [users, setUsers]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState({ role: '', status: '' });
  const [search, setSearch]   = useState('');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [page, setPage]       = useState(1);
  const [actionMsg, setActionMsg] = useState('');
  const [restarting, setRestarting] = useState({});
  const [testing, setTesting] = useState({});
  const [deleting, setDeleting] = useState({});
  const [verifying, setVerifying] = useState({});
  const [vncLoading, setVncLoading] = useState({});
  const [logsFor, setLogsFor] = useState(null);   // { id, email }

  useEffect(() => { loadUsers(); }, [filter, page]); // eslint-disable-line
  useEffect(() => {
    const id = setInterval(() => loadUsers({ silent: true }), 5000);
    return () => clearInterval(id);
  }, [filter, page]); // eslint-disable-line

  async function loadUsers({ silent = false } = {}) {
    if (!silent) setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter.role)   params.role   = filter.role;
      if (filter.status) params.status = filter.status;
      const r = await api.get('/admin/users', { params });
      setUsers(r.data.users);
      setTotal(r.data.total);
    } finally {
      if (!silent) setLoading(false);
    }
  }

  async function verify(id) {
    setVerifying(v => ({ ...v, [id]: true }));
    setActionMsg('Verifying user — waiting up to 90s for MT5 to come online…');
    try {
      const r = await api.put(`/admin/users/${id}/verify`);
      setActionMsg(r.data.message || 'User verified.');
      loadUsers();
    } catch (e) {
      const d = e.response?.data || {};
      const detail = d.detail ? ` — ${d.detail}` : '';
      const reason = d.reason ? ` [${d.reason}]` : '';
      setActionMsg(`Error: ${d.error || e.message}${reason}${detail}`);
      loadUsers();
    } finally {
      setVerifying(v => ({ ...v, [id]: false }));
    }
  }

  async function suspend(id) {
    if (!window.confirm('Suspend this user? Their container will be stopped.')) return;
    try {
      await api.put(`/admin/users/${id}/suspend`);
      setActionMsg('User suspended.');
      loadUsers();
    } catch (e) {
      setActionMsg('Error: ' + e.response?.data?.error);
    }
  }

  async function unsuspend(id) {
    if (!window.confirm('Unsuspend this user and start their MT5 container?')) return;
    setActionMsg('Unsuspending user and starting container…');
    try {
      const r = await api.put(`/admin/users/${id}/unsuspend`);
      setActionMsg(r.data.message || 'User unsuspended.');
      loadUsers();
    } catch (e) {
      setActionMsg('Error: ' + (e.response?.data?.error || e.message));
    }
  }

  async function restart(id) {
    setRestarting(r => ({ ...r, [id]: true }));
    setActionMsg('');
    try {
      await api.post(`/admin/users/${id}/restart-container`);
      setActionMsg('Container recreated and starting up. This takes about 60 seconds.');
      loadUsers();
    } catch (e) {
      setActionMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setRestarting(r => ({ ...r, [id]: false }));
    }
  }

  async function openVnc(userId) {
    setVncLoading(v => ({ ...v, [userId]: true }));
    try {
      const r = await api.get(`/admin/vnc-access/${userId}`);
      const { hostIp, hostPort, vncPassword } = r.data;
      // H6: noVNC binds 127.0.0.1 on the server — SSH tunnel only, so show
      // the recipe instead of opening a tab that can never connect.
      alert(
        `noVNC is loopback-only on the server (H6 hardening).\n\n` +
        `1. Tunnel:   ssh -L ${hostPort}:${hostIp || '127.0.0.1'}:${hostPort} <server>\n` +
        `2. Open:     http://localhost:${hostPort}/vnc.html\n` +
        `3. Password: ${vncPassword}`
      );
    } catch (err) {
      alert(`Cannot open GUI: ${err.response?.data?.error || err.message}`);
    } finally {
      setVncLoading(v => ({ ...v, [userId]: false }));
    }
  }

  async function markLoginComplete(userId) {
    setTesting(t => ({ ...t, [userId]: true }));
    setActionMsg('Running live MT5 API test inside the container…');
    try {
      const r = await api.post(`/admin/users/${userId}/mt5-test`);
      const t = r.data.test || {};
      if (t.ok) {
        setActionMsg(
          `MT5 API OK — account ${t.account} on ${t.server} | balance ${t.balance} ${t.currency} | equity ${t.equity}. User flipped to running.`
        );
      } else {
        const why = t.error || (t.connected === false ? 'not connected to broker' :
          t.trade_allowed === false ? 'Algo Trading is not enabled' : 'no account info');
        setActionMsg(`MT5 API test failed: ${why}`);
      }
      loadUsers();
    } catch (e) {
      setActionMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setTesting(t => ({ ...t, [userId]: false }));
    }
  }

  async function deleteUser(u) {
    const confirmText =
      `Permanently DELETE ${u.email}?\n\n` +
      `This stops and removes their MT5 container, deletes all volumes, ` +
      `copy relationships, trades, executions, snapshots, and notifications. ` +
      `This cannot be undone.\n\nType the email to confirm.`;
    const typed = window.prompt(confirmText);
    if (typed !== u.email) {
      if (typed != null) setActionMsg('Delete cancelled — email did not match.');
      return;
    }
    setDeleting(d => ({ ...d, [u._id]: true }));
    setActionMsg(`Deleting ${u.email}…`);
    try {
      await api.delete(`/admin/users/${u._id}`);
      setActionMsg(`Deleted ${u.email}.`);
      loadUsers();
    } catch (e) {
      setActionMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setDeleting(d => ({ ...d, [u._id]: false }));
    }
  }

  async function loginAsUser(u) {
    if (!window.confirm(
      `Log in as ${u.email}?\n\n` +
      `You will be switched to their dashboard for support purposes. ` +
      `A "Return to admin" banner will let you switch back.`
    )) return;
    try {
      const r = await api.post(`/admin/users/${u._id}/impersonate`);
      const adminToken        = localStorage.getItem('token');
      const adminRefreshToken = localStorage.getItem('refreshToken');
      if (adminToken)        localStorage.setItem('adminToken', adminToken);
      if (adminRefreshToken) localStorage.setItem('adminRefreshToken', adminRefreshToken);
      localStorage.setItem('impersonatingEmail', u.email);
      localStorage.setItem('token', r.data.token);
      localStorage.removeItem('refreshToken');
      window.location.assign(`/${r.data.user.role}`);
    } catch (e) {
      setActionMsg('Error: ' + (e.response?.data?.error || e.message));
    }
  }

  return (
    <>
      <div className="ct-page-header">
        <div>
          <span className="eyebrow">Admin</span>
          <h1>Users</h1>
          <div className="subtitle">Verify accounts, manage MT5 containers, copy credentials</div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search username, email, MT5 #"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ minWidth: 220 }}
          />
          <select className="form-control form-control-sm" value={filter.role}
            onChange={e => setFilter(f => ({ ...f, role: e.target.value }))}>
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="customer">Customer</option>
          </select>
          <select className="form-control form-control-sm" value={filter.status}
            onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
            <option value="">All status</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="suspended">Suspended</option>
          </select>
          {(() => {
            const attentionCount = users.filter(u => u.containerStatus === 'needs_attention').length;
            return (
              <button
                className={`btn btn-sm ${attentionOnly ? 'btn-warning' : 'btn-secondary'}`}
                onClick={() => setAttentionOnly(v => !v)}
                title="Show only users whose MT5 container needs admin attention"
                disabled={!attentionOnly && attentionCount === 0}
              >
                Needs attention
                {attentionCount > 0 && (
                  <span className="badge badge-light" style={{marginLeft:6}}>{attentionCount}</span>
                )}
              </button>
            );
          })()}
          {(search || filter.role || filter.status || attentionOnly) && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => { setSearch(''); setFilter({ role: '', status: '' }); setAttentionOnly(false); }}
            >Clear</button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="card-title">User Management</h5>
          <div className="card-category" style={{marginTop:4}}>{total} total</div>
        </div>
        <div className="card-body" style={{padding:0}}>
          {actionMsg && (
            <div className="alert alert-info" style={{margin:16}}>
              {actionMsg}
              <button
                style={{float:'right', background:'transparent', border:'none', color:'inherit', cursor:'pointer'}}
                onClick={() => setActionMsg('')}>×</button>
            </div>
          )}
          {loading ? (
            <div style={{padding:20, color:'var(--text-secondary)'}}>Loading…</div>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>MT5</th>
                    <th>Server</th>
                    <th>Container</th>
                    <th>Verified</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => {
                    if (attentionOnly && u.containerStatus !== 'needs_attention') return false;
                    if (!search) return true;
                    const q = search.toLowerCase().trim();
                    return (
                      (u.username && u.username.toLowerCase().includes(q)) ||
                      (u.email    && u.email.toLowerCase().includes(q)) ||
                      (u.mt5AccountNumber && String(u.mt5AccountNumber).includes(q))
                    );
                  }).map(u => {
                    const showVnc = u.containerId && (
                      u.containerStatus === 'running' ||
                      u.containerStatus === 'starting' ||
                      u.containerStatus === 'awaiting_manual_login' ||
                      u.containerStatus === 'needs_attention'
                    );
                    const role =
                      u.role === 'admin'   ? 'badge-warning' :
                      u.role === 'customer' ? 'badge-info'    : 'badge-secondary';
                    const status =
                      u.status === 'verified'  ? 'badge-success' :
                      u.status === 'suspended' ? 'badge-danger'  : 'badge-warning';

                    return (
                      <React.Fragment key={u._id}>
                        <tr>
                          <td>
                            <div style={{fontWeight:600, color:'var(--text-primary)'}}>
                              {u.username || <span style={{color:'var(--text-tertiary)', fontWeight:400, fontStyle:'italic'}}>no username</span>}
                            </div>
                            <div className="small" style={{color:'var(--text-secondary)', fontSize:11.5}}>{u.email}</div>
                          </td>
                          <td><span className={`badge ${role}`}>{u.role}</span></td>
                          <td><span className={`badge ${status}`}>{u.status}</span></td>
                          <td className="num">
                            {u.mt5AccountNumber || '—'}
                            {u.mt5InvestorPassword && (
                              <span
                                className="badge badge-info"
                                style={{marginLeft:6, fontSize:10}}
                                title="Account uses MT5 investor (read-only) password — signal source only, no order execution"
                              >investor</span>
                            )}
                          </td>
                          <td className="small" style={{color:'var(--text-secondary)'}}>{u.mt5Server || '—'}</td>
                          <td>
                            <StatusPill status={u.containerStatus} />
                            {u.containerError && (
                              <div style={{
                                color:'var(--loss)', fontSize:11, marginTop:6,
                                maxWidth:240, whiteSpace:'normal', wordBreak:'break-word',
                              }} title={u.containerError}>
                                {u.containerError}
                              </div>
                            )}
                          </td>
                          <td className="small" style={{color:'var(--text-tertiary)'}}>
                            {u.verifiedAt ? new Date(u.verifiedAt).toLocaleDateString() : '—'}
                          </td>
                          <td style={{whiteSpace:'nowrap'}}>
                            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                              {u.status === 'pending' && (
                                <button className="btn btn-sm btn-success"
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}
                                  onClick={() => verify(u._id)}
                                  disabled={!!verifying[u._id]}>
                                  {verifying[u._id]
                                    ? <RotateCw size={13} style={{animation:'spin 1s linear infinite'}} />
                                    : <CheckCircle2 size={13} />}
                                  {verifying[u._id] ? 'Verifying…' : 'Verify'}
                                </button>
                              )}
                              {u.status === 'verified' && (
                                <button className="btn btn-sm btn-warning"
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}
                                  onClick={() => suspend(u._id)}>
                                  <Pause size={13} /> Suspend
                                </button>
                              )}
                              {u.status === 'suspended' && (
                                <button className="btn btn-sm btn-success"
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}
                                  onClick={() => unsuspend(u._id)}
                                  title="Restore this user and start their MT5 container">
                                  <Play size={13} /> Unsuspend & Start
                                </button>
                              )}
                              {u.status === 'verified' && u.mt5AccountNumber && (
                                <button className="btn btn-sm btn-secondary"
                                  onClick={() => restart(u._id)}
                                  disabled={!!restarting[u._id]}
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                  <RotateCw size={13} style={{animation: restarting[u._id] ? 'spin 1s linear infinite':'none'}} />
                                  {restarting[u._id] ? 'Restarting…' : 'Restart'}
                                </button>
                              )}
                              {showVnc && (
                                <button className="btn btn-sm btn-primary"
                                  onClick={() => openVnc(u._id)}
                                  disabled={!!vncLoading[u._id]}
                                  title="Show the SSH-tunnel recipe for the noVNC viewer (loopback-only per H6)"
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                  <ExternalLink size={13} />
                                  {vncLoading[u._id] ? 'VNC…' : 'VNC'}
                                </button>
                              )}
                              {u.containerId && (
                                <button className="btn btn-sm btn-secondary"
                                  onClick={() => setLogsFor({ id: u._id, email: u.email })}
                                  title="Stream live container logs"
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                  <FileText size={13} /> Logs
                                </button>
                              )}
                              {u.role !== 'admin' && u.status !== 'suspended' && (
                                <button className="btn btn-sm btn-info"
                                  onClick={() => loginAsUser(u)}
                                  title="Log in as this user (for support). You can return to admin afterwards."
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                  <LogIn size={13} /> Login as User
                                </button>
                              )}
                              <button className="btn btn-sm btn-danger"
                                onClick={() => deleteUser(u)}
                                disabled={!!deleting[u._id]}
                                title="Permanently delete this user, their container, volumes, and all data"
                                style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                <Trash2 size={13} />
                                {deleting[u._id] ? 'Deleting…' : 'Delete'}
                              </button>
                              {showVnc && (
                                <button className="btn btn-sm btn-success"
                                  onClick={() => markLoginComplete(u._id)}
                                  disabled={!!testing[u._id]}
                                  title="Confirm MT5 login is complete and run a live API test (terminal_info + account_info)"
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                  <PlugZap size={13} />
                                  {testing[u._id] ? 'Testing…' : 'Mark Login Complete'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {(u.mt5AccountNumber || u.mt5Password || u.vncPassword) && (
                          <tr>
                            <td colSpan={8} style={{borderTop:'none', paddingTop:0, paddingBottom:14}}>
                              <div style={{display:'flex',flexWrap:'wrap',gap:10,fontSize:11.5}}>
                                <CredField label="Login"        value={u.mt5AccountNumber} />
                                <CredField label="Server"       value={u.mt5Server} />
                                <CredField label="MT5 Password" value={u.mt5Password} />
                                {u.vncPassword && <CredField label="VNC Password" value={u.vncPassword} />}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div style={{
                display:'flex',justifyContent:'space-between',alignItems:'center',
                padding:'14px 20px',borderTop:'1px solid var(--border-subtle)'
              }}>
                <span style={{color:'var(--text-tertiary)',fontSize:12}}>
                  Page {page} — {users.length} / {total}
                </span>
                <div style={{display:'flex',gap:6}}>
                  <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                  <button className="btn btn-sm btn-secondary" disabled={users.length < 20} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>

      {logsFor && (
        <LogsModal
          userId={logsFor.id}
          email={logsFor.email}
          onClose={() => setLogsFor(null)}
        />
      )}
    </>
  );
}

function LogsModal({ userId, email, onClose }) {
  const [logs, setLogs] = useState('');
  const [error, setError] = useState('');
  const [paused, setPaused] = useState(false);
  const [autoscroll, setAutoscroll] = useState(true);
  const [loading, setLoading] = useState(true);
  const preRef = useRef(null);
  const pausedRef = useRef(paused);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  useEffect(() => {
    let cancelled = false;

    async function tick() {
      if (pausedRef.current) return;
      try {
        const r = await api.get(`/admin/container-logs/${userId}`, { params: { tail: 800 } });
        if (cancelled) return;
        setLogs(r.data.logs || '');
        setError('');
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.error || e.message);
        setLoading(false);
      }
    }

    tick();
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [userId]);

  // Auto-scroll to bottom on update
  useEffect(() => {
    if (!autoscroll || !preRef.current) return;
    preRef.current.scrollTop = preRef.current.scrollHeight;
  }, [logs, autoscroll]);

  // Close on Esc
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position:'fixed', inset:0, zIndex:200,
        background:'rgba(0,0,0,0.65)',
        display:'flex', alignItems:'center', justifyContent:'center', padding:16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width:'min(1100px, 100%)', maxHeight:'90vh',
          display:'flex', flexDirection:'column',
          background:'var(--bg-surface)',
          border:'1px solid var(--border-subtle)',
          borderRadius:'var(--radius-md)',
          boxShadow:'0 20px 60px rgba(0,0,0,0.55)',
          overflow:'hidden',
        }}
      >
        <div style={{
          display:'flex', alignItems:'center', gap:12,
          padding:'12px 16px',
          borderBottom:'1px solid var(--border-subtle)',
          background:'var(--bg-surface-2)',
        }}>
          <FileText size={16} />
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:13, fontWeight:600, color:'var(--text-primary)'}}>
              Container logs
            </div>
            <div style={{fontSize:11, color:'var(--text-tertiary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
              {email}{paused ? ' • paused' : ' • live (2s poll)'}
            </div>
          </div>
          <label style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-secondary)', cursor:'pointer'}}>
            <input type="checkbox" checked={autoscroll} onChange={e => setAutoscroll(e.target.checked)} />
            Auto-scroll
          </label>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setPaused(p => !p)}
            style={{display:'inline-flex', alignItems:'center', gap:4}}
            title={paused ? 'Resume polling' : 'Pause polling'}
          >
            {paused ? <><Play size={13}/> Resume</> : <><PauseIcon size={13}/> Pause</>}
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={onClose}
            aria-label="Close"
            style={{display:'inline-flex', alignItems:'center', gap:4}}
          >
            <X size={14}/>
          </button>
        </div>

        {error && (
          <div className="alert alert-danger" style={{margin:12}}>
            {error}
          </div>
        )}

        <pre
          ref={preRef}
          style={{
            flex:1, margin:0, padding:'12px 16px',
            overflow:'auto',
            background:'#0a0e1a',
            color:'#d4d8e6',
            fontFamily:'"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize:12, lineHeight:1.45,
            whiteSpace:'pre-wrap', wordBreak:'break-word',
            minHeight:240,
          }}
        >
          {loading ? 'Loading logs…' : (logs || '(no log output yet)')}
        </pre>
      </div>
    </div>
  );
}

function CredField({ label, value }) {
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
      <span style={{
        textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--text-tertiary)',
        fontSize:10.5,fontWeight:600
      }}>{label}</span>
      <CopyChip value={value} label={label.toLowerCase()} />
    </span>
  );
}
